import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Users, 
  BarChart3, 
  PlusCircle,
  Target,
  Trophy,
  LogOut,
  Shield,
  ChevronDown,
  User,
  MoreHorizontal,
  Rocket,
  Sun,
  Moon
} from "lucide-react";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";

// Pages
import Dashboard from "./pages/Dashboard";
import Scorecards from "./pages/Scorecards";
import ScorecardDetail from "./pages/ScorecardDetail";
import MonthlyRocks from "./pages/MonthlyRocks";
import Reports from "./pages/Reports";
import Leaderboard from "./pages/Leaderboard";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminPanel from "./pages/AdminPanel";
import CompanyRocks from "./pages/CompanyRocks";
import Profile from "./pages/Profile";
import PublicReport from "./pages/PublicReport";
import ClientPortal from "./pages/ClientPortal";
import PresentationsBlue from "./pages/PresentationsBlue";
import PresentationsWhite from "./pages/PresentationsWhite";
import PresentationsTraction from "./pages/PresentationsTraction";
import PresentationsFocus from "./pages/PresentationsFocus";
import PresentationsPhoneIntel from "./pages/PresentationsPhoneIntel";
import PresentationsPartners from "./pages/PresentationsPartners";
import PresentationsCalldripDemo from "./pages/PresentationsCalldripDemo";
import PresentationsKubota from "./pages/PresentationsKubota";

const BACKEND_URL = window.location.origin.includes('localhost') 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Date Context - Shared month/year selection across pages
const DateContext = createContext(null);

export const useSelectedDate = () => useContext(DateContext);

// Helper function to get weeks in a month
export const getWeeksInMonth = (year, month) => {
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  // Find the first Friday of the month or before
  let currentDate = new Date(firstDay);
  const dayOfWeek = currentDate.getDay();
  // Find the Friday of the first week (could be in previous month)
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  currentDate.setDate(currentDate.getDate() + daysUntilFriday);
  
  // If the first Friday is after the 7th, start from the previous week's Friday
  if (currentDate.getDate() > 7 && currentDate.getMonth() === month - 1) {
    currentDate.setDate(currentDate.getDate() - 7);
  }
  
  // Generate all Fridays that fall within or end the weeks of this month
  while (currentDate <= lastDay || currentDate.getMonth() === month - 1) {
    // Only include if this Friday ends a week that includes days from this month
    const weekStart = new Date(currentDate);
    weekStart.setDate(weekStart.getDate() - 6); // Saturday of previous week
    
    if (currentDate.getMonth() === month - 1 || 
        (weekStart.getMonth() === month - 1 && weekStart <= lastDay)) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `Week ending ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}`;
      weeks.push({ value: dateStr, label });
    }
    
    currentDate.setDate(currentDate.getDate() + 7);
    
    // Safety: don't go more than 2 weeks past the month
    if (currentDate.getMonth() > month && currentDate.getDate() > 14) break;
  }
  
  return weeks;
};

// Get current week's Friday
export const getCurrentWeekFriday = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  const friday = new Date(today);
  friday.setDate(today.getDate() + daysUntilFriday);
  return friday.toISOString().split('T')[0];
};

// Date Provider - Persists selected month/year/week/viewMode across navigation
const DateProvider = ({ children }) => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Try to restore from sessionStorage, default to current month
    const saved = sessionStorage.getItem('selectedMonth');
    return saved ? parseInt(saved) : new Date().getMonth() + 1;
  });
  
  const [selectedYear, setSelectedYear] = useState(() => {
    const saved = sessionStorage.getItem('selectedYear');
    return saved ? parseInt(saved) : new Date().getFullYear();
  });

  const [viewMode, setViewMode] = useState(() => {
    // Remember user's preference - default to 'weekly'
    const saved = localStorage.getItem('viewMode');
    return saved || 'weekly';
  });

  const [selectedWeek, setSelectedWeek] = useState(() => {
    const saved = sessionStorage.getItem('selectedWeek');
    return saved || getCurrentWeekFriday();
  });

  // Persist to sessionStorage/localStorage when changed
  useEffect(() => {
    sessionStorage.setItem('selectedMonth', selectedMonth.toString());
    sessionStorage.setItem('selectedYear', selectedYear.toString());
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    localStorage.setItem('viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    sessionStorage.setItem('selectedWeek', selectedWeek);
  }, [selectedWeek]);

  // When month/year changes, update selectedWeek to a valid week in that month
  useEffect(() => {
    const weeks = getWeeksInMonth(selectedYear, selectedMonth);
    if (weeks.length > 0) {
      const currentWeekInMonth = weeks.find(w => w.value === selectedWeek);
      if (!currentWeekInMonth) {
        // Select the last week of the month by default
        setSelectedWeek(weeks[weeks.length - 1].value);
      }
    }
  }, [selectedMonth, selectedYear]);

  return (
    <DateContext.Provider value={{ 
      selectedMonth, 
      setSelectedMonth, 
      selectedYear, 
      setSelectedYear,
      viewMode,
      setViewMode,
      selectedWeek,
      setSelectedWeek,
      getWeeksInMonth
    }}>
      {children}
    </DateContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to refresh user data from API
  const refreshUser = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const freshUserData = response.data;
      localStorage.setItem("user", JSON.stringify(freshUserData));
      setUser(freshUserData);
      return freshUserData;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      // If refresh fails, logout
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setToken(null);
      }
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      
      if (storedToken) {
        setToken(storedToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
        // Always fetch fresh user data to get latest department changes
        await refreshUser(storedToken);
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (userData) => {
    const storedToken = localStorage.getItem("token");
    setToken(storedToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    // Fetch fresh user data immediately after login
    const freshData = await refreshUser(storedToken);
    setUser(freshData || userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setToken(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg)' }}>
        <div className="loading-spinner border-t-indigo-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAdmin: ["admin", "super_admin"].includes(user?.role), isSuperAdmin: user?.role === "super_admin", refreshUser: () => refreshUser(token) }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component - redirects external users to their portal
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // External users should be redirected to their client portal
  if (user.is_external) {
    // Get the org and account slugs
    const orgSlug = getSlugFromName(user.organization_name) || 'unknown';
    const accountSlug = getSlugFromName(user.account_name) || 'unknown';
    return <Navigate to={`/client-portal`} replace />;
  }
  
  return children;
};

// Helper function to convert name to slug
const getSlugFromName = (name) => {
  if (!name) return null;
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Impersonation Banner Component
const ImpersonationBanner = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState({});
  
  // Check impersonation status on mount and when localStorage changes
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem("impersonating") === "true";
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setIsImpersonating(impersonating);
      setImpersonatedUser(user);
    };
    
    // Check immediately
    checkImpersonation();
    
    // Also listen for storage events (in case of changes from other tabs)
    window.addEventListener('storage', checkImpersonation);
    
    // Check periodically in case of redirect issues
    const interval = setInterval(checkImpersonation, 500);
    
    return () => {
      window.removeEventListener('storage', checkImpersonation);
      clearInterval(interval);
    };
  }, []);
  
  // Add keyboard shortcut to exit impersonation (Escape key)
  useEffect(() => {
    if (!isImpersonating) return;
    
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        handleExitImpersonation();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isImpersonating]);
  
  const handleExitImpersonation = () => {
    // Restore the original admin session
    const adminToken = localStorage.getItem("admin_token_backup");
    const adminUser = localStorage.getItem("admin_user_backup");
    
    if (adminToken && adminUser) {
      localStorage.setItem("token", adminToken);
      localStorage.setItem("user", adminUser);
    }
    
    // Clean up impersonation data
    localStorage.removeItem("admin_token_backup");
    localStorage.removeItem("admin_user_backup");
    localStorage.removeItem("impersonating");
    localStorage.removeItem("impersonated_by");
    
    // Redirect to admin panel
    window.location.href = "/admin";
  };
  
  if (!isImpersonating) return null;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between shadow-lg" data-testid="impersonation-banner">
      <div className="flex items-center gap-3">
        <div className="bg-white/20 rounded-full p-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </div>
        <div>
          <span className="font-semibold text-sm">Viewing as: {impersonatedUser?.name || 'User'}</span>
          <span className="text-white/80 text-xs ml-2">({impersonatedUser?.email})</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-white/70 text-xs hidden sm:inline">Press ESC or click →</span>
        <button
          onClick={handleExitImpersonation}
          data-testid="exit-impersonation-btn"
          className="bg-white text-amber-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-amber-100 transition-colors flex items-center gap-2 shadow-md"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" x2="9" y1="12" y2="12"/>
          </svg>
          Exit Impersonation
        </button>
      </div>
    </div>
  );
};

// Top Header Component (Logo + User Menu)
const TopHeader = () => {
  const { user, logout, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setUserMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors ${isDark ? 'bg-slate-900 border-b border-slate-800' : 'bg-white border-b border-slate-200 shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex flex-col items-center">
            <img 
              src={isDark 
                ? "https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png"
                : "https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png"
              }
              alt="Calldrip" 
              className={`h-6 w-auto ${!isDark ? 'brightness-0' : ''}`}
            />
            <span className={`text-[9px] tracking-[0.2em] uppercase mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>scorecards</span>
          </div>

          {/* Right side: Theme Toggle + User Menu */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
              data-testid="theme-toggle"
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid="user-menu-button"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                  isAdmin ? "bg-purple-600" : "bg-indigo-600"
                }`}>
                  {user?.name?.charAt(0)}
                </div>
                <span className="hidden sm:inline max-w-[120px] truncate">{user?.name}</span>
                <ChevronDown size={16} className={`transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {userMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-xl z-20 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                    <div className={`px-4 py-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.name}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user?.email}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isAdmin ? "bg-purple-500/20 text-purple-400" : "bg-indigo-500/20 text-indigo-400"
                      }`}>
                        {user?.role}
                      </span>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={handleLogout}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-red-50'}`}
                        data-testid="logout-button"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Bottom Navigation Component
const BottomNav = () => {
  const { isAdmin } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  
  const mainNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Home" },
    { path: "/leaderboard", icon: Trophy, label: "Leaders" },
    { path: "/scorecards", icon: Users, label: "Cards" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  const moreItems = [
    { path: "/company-rocks", icon: Rocket, label: "Rocks" },
    { path: "/reports", icon: BarChart3, label: "Reports" },
  ];

  if (isAdmin) {
    moreItems.push({ path: "/admin", icon: Shield, label: "Admin" });
  }

  const isMoreActive = moreItems.some(item => 
    location.pathname === item.path || 
    (item.path !== "/" && location.pathname.startsWith(item.path))
  );

  return (
    <>
      {/* More Menu Overlay */}
      {moreMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40"
          onClick={() => setMoreMenuOpen(false)}
        />
      )}
      
      {/* More Menu Popup */}
      {moreMenuOpen && (
        <div className={`fixed bottom-20 left-4 right-4 rounded-2xl z-50 p-2 shadow-xl ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
          <div className="grid grid-cols-3 gap-2">
            {moreItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== "/" && location.pathname.startsWith(item.path));
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMoreMenuOpen(false)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all ${
                    isActive 
                      ? "bg-indigo-500/20 text-indigo-400" 
                      : isDark 
                        ? "text-slate-400 hover:bg-slate-700 hover:text-white"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <item.icon size={24} />
                  <span className="text-xs font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Nav Bar */}
      <nav className={`fixed bottom-0 left-0 right-0 z-50 safe-area-bottom transition-colors ${isDark ? 'bg-slate-900 border-t border-slate-800' : 'bg-white border-t border-slate-200 shadow-lg'}`}>
        <div className="flex items-center justify-around py-2 px-2">
          {mainNavItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/" && location.pathname.startsWith(item.path));
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 py-1.5 px-4 rounded-xl transition-all ${
                  isActive 
                    ? "text-indigo-400" 
                    : isDark 
                      ? "text-slate-500 hover:text-slate-300"
                      : "text-slate-400 hover:text-slate-600"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <div className={`p-1.5 rounded-lg ${isActive ? "bg-indigo-500/20" : ""}`}>
                  <item.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
              </NavLink>
            );
          })}
          
          {/* More Button */}
          <button
            onClick={() => setMoreMenuOpen(!moreMenuOpen)}
            className={`flex flex-col items-center gap-1 py-1.5 px-4 rounded-xl transition-all ${
              isMoreActive || moreMenuOpen
                ? "text-indigo-400" 
                : isDark 
                  ? "text-slate-500 hover:text-slate-300"
                  : "text-slate-400 hover:text-slate-600"
            }`}
            data-testid="nav-more"
          >
            <div className={`p-1.5 rounded-lg ${isMoreActive || moreMenuOpen ? "bg-indigo-500/20" : ""}`}>
              <MoreHorizontal size={22} strokeWidth={isMoreActive || moreMenuOpen ? 2.5 : 2} />
            </div>
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

// Main Layout Component
const Layout = ({ children }) => {
  const { isDark } = useTheme();
  const [isImpersonating, setIsImpersonating] = useState(false);
  
  useEffect(() => {
    const checkImpersonation = () => {
      setIsImpersonating(localStorage.getItem("impersonating") === "true");
    };
    checkImpersonation();
    const interval = setInterval(checkImpersonation, 500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <ImpersonationBanner />
      <TopHeader />
      <main className={`pb-24 px-4 max-w-7xl mx-auto ${isImpersonating ? 'pt-24' : 'pt-16'}`}>
        {children}
      </main>
      <BottomNav />
    </div>
  );
};

// App Content with Routes
const AppContent = () => {
  const { user, login } = useAuth();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
        setInitialized(true);
      } catch (e) {
        console.error("Error seeding data:", e);
        setInitialized(true);
      }
    };
    seedData();
  }, []);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg)' }}>
        <div className="text-center">
          <div className="loading-spinner border-t-indigo-500 mb-4"></div>
          <p style={{ color: 'var(--theme-text-secondary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/" replace /> : <Login onLogin={login} />
      } />
      <Route path="/forgot-password" element={
        user ? <Navigate to="/" replace /> : <ForgotPassword />
      } />
      <Route path="/reset-password" element={
        user ? <Navigate to="/" replace /> : <ResetPassword />
      } />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Layout><Leaderboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/scorecards" element={
        <ProtectedRoute>
          <Layout><Scorecards /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/scorecards/:id" element={
        <ProtectedRoute>
          <Layout><ScorecardDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/rocks" element={
        <ProtectedRoute>
          <Layout><MonthlyRocks /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/company-rocks" element={
        <ProtectedRoute>
          <Layout><CompanyRocks /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Layout><Reports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><Profile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout><AdminPanel /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Public report routes - no auth required */}
      <Route path="/reports/:orgSlug/:accountSlug" element={<PublicReport />} />
      
      {/* Public presentations - no auth required */}
      <Route path="/presentations/blue" element={<PresentationsBlue />} />
      <Route path="/presentations/white" element={<PresentationsWhite />} />
      <Route path="/presentations/traction" element={<PresentationsTraction />} />
      <Route path="/presentations/focus" element={<PresentationsFocus />} />
      <Route path="/presentations/phone-intel" element={<PresentationsPhoneIntel />} />
      <Route path="/presentations/partners" element={<PresentationsPartners />} />
      <Route path="/presentations/demo" element={<PresentationsCalldripDemo />} />
      <Route path="/presentations/kubota" element={<PresentationsKubota />} />
      <Route path="/presentations" element={<PresentationsBlue />} />
      
      {/* Client Portal for external users */}
      <Route path="/client-portal" element={
        user?.is_external ? <ClientPortal /> : <Navigate to="/" replace />
      } />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <DateProvider>
            <AppContent />
          </DateProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
